import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import { useTranslation } from "react-i18next";
import apiClient from "@/app/api/client";
import { usePaginatedQuery } from "@/shared/hooks/usePaginatedQuery";
import { getErrorMessage } from "@/shared/utils/getErrorMessage";
import { zonesService } from "../services/zones.service";
import type {
	Zone,
	ZoneAssignInput,
	ZoneCreateInput,
	ZoneListFilters,
	ZoneUpdateInput,
} from "../types";

/**
 * ⚠️ `isActive` HAR DOIM yuboriladi. `ZoneListQuerySchema` uni `"true"` ga
 * standartlaydi — parametr tushib qolsa, foydalanuvchi «filtr yo'q» deb
 * o'ylagan holatda ro'yxat arxivlangan hududlarni jimgina yashirardi.
 * Shuning uchun `ZoneListFilters.isActive` ham ixtiyoriy EMAS.
 */
export function buildZoneListParams(filters: ZoneListFilters): Record<string, string | boolean> {
	const params: Record<string, string | boolean> = { isActive: filters.isActive };

	if (filters.q) {
		params.q = filters.q;
	}

	if (filters.departmentId) {
		params.departmentId = filters.departmentId;
	}

	if (filters.shared) {
		params.shared = true;
	}

	return params;
}

export function useZonesList(filters: ZoneListFilters) {
	return usePaginatedQuery<Zone>({
		queryKey: ["zones"],
		url: "/zones",
		params: buildZoneListParams(filters),
	});
}

interface EmployeeOption {
	id: string;
	fullName: string;
}

/**
 * Xodim tanlash — server tomonida qidiriladi (`q` filtri). Bank miqyosida
 * xodimlar 100 tadan ko'p, shuning uchun bitta sahifani mijozda filtrlash
 * yetarli emas (`useManagerOptions` bilan bir xil sabab).
 *
 * Boshqa modulning hook'i import qilinmaydi — `useDepartmentOptions` naqshi:
 * kichik so'rov `apiClient` orqali shu modulda e'lon qilinadi.
 */
export function useEmployeeOptions(search: string) {
	return useQuery({
		queryKey: ["employees", "zone-options", search],
		queryFn: async () => {
			const params: Record<string, string | number> = { page: 1, limit: 100 };

			if (search.trim()) {
				params.q = search.trim();
			}

			const response = await apiClient.get<{
				success: true;
				data: { items: EmployeeOption[] };
			}>("/employees", { params });

			return response.data.data.items;
		},
		staleTime: 30_000,
	});
}

function useZonesMutation<TVariables, TResult>(
	mutationFn: (variables: TVariables) => Promise<TResult>,
	successMessage: string,
	errorFallback: string
) {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["zones"] });
			message.success(successMessage);
		},
		onError: (error) => {
			// Backend aniq matn qaytaradi (`messages.zones`): noto'g'ri halqa,
			// radius chegarasi, amaldagi biriktirishlar. Umumiy xabar bu
			// sabablarni yashirardi.
			message.error(getErrorMessage(error, errorFallback));
		},
	});
}

export function useCreateZone() {
	const { t } = useTranslation("zones");
	return useZonesMutation(
		(input: ZoneCreateInput) => zonesService.create(input),
		t("messages.created"),
		t("messages.createFailed")
	);
}

export function useUpdateZone() {
	const { t } = useTranslation("zones");
	return useZonesMutation(
		({ id, input }: { id: string; input: ZoneUpdateInput }) => zonesService.update(id, input),
		t("messages.updated"),
		t("messages.updateFailed")
	);
}

export function useArchiveZone() {
	const { t } = useTranslation("zones");
	return useZonesMutation(
		(id: string) => zonesService.archive(id),
		t("messages.archived"),
		t("messages.archiveFailed")
	);
}

export function useZoneAssignments(zoneId: string | null) {
	return useQuery({
		queryKey: ["zones", zoneId, "assignments"],
		queryFn: () => zonesService.listAssignments(zoneId as string),
		enabled: zoneId !== null,
	});
}

function useAssignmentMutation<TVariables>(
	zoneId: string | null,
	mutationFn: (variables: TVariables) => Promise<unknown>,
	successMessage: string,
	errorFallback: string
) {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["zones", zoneId, "assignments"] });
			message.success(successMessage);
		},
		onError: (error) => {
			message.error(getErrorMessage(error, errorFallback));
		},
	});
}

export function useAssignZone(zoneId: string | null) {
	const { t } = useTranslation("zones");
	return useAssignmentMutation(
		zoneId,
		(input: ZoneAssignInput) => zonesService.assign(zoneId as string, input),
		t("messages.assigned"),
		t("messages.assignFailed")
	);
}

/** «Yakunlash» — qator o'chmaydi, `valid_to` qo'yiladi (servis izohiga qarang). */
export function useEndZoneAssignment(zoneId: string | null) {
	const { t } = useTranslation("zones");
	return useAssignmentMutation(
		zoneId,
		(assignmentId: string) => zonesService.endAssignment(zoneId as string, assignmentId),
		t("messages.assignmentEnded"),
		t("messages.assignmentEndFailed")
	);
}
